import type { DisplayJob } from '../types';
import type { JobFormState } from '../redux/features/jobFormSlice';
import type { ComparisonWeights } from './jobComparison';
import { getRegionTagLabel } from '../facets/region-tag-labels';

export interface MatchScoreBreakdown {
  overall: number;
  salary: number;
  vacation: number;
  remoteness: number;
  benefits: number;
  languages: number;
  weights: ComparisonWeights;
  /** Only populated when the user has a desired/current title to compare against. */
  title?: number;
  roleLevel?: number;
  location?: number;
  jobType?: number;
}

/** Level hierarchy (en/fi/sv) for partial role-level matches. Keys are lowercased facet values. */
export const ROLE_LEVEL_HIERARCHY: Record<string, number> = {
  intern: 1,
  harjoittelija: 1,
  praktikant: 1,
  junior: 2,
  'junior ja entry-taso': 2,
  'mid-level': 3,
  keskitaso: 3,
  medelnivå: 3,
  senior: 4,
  'senior-taso': 4,
  expert: 5,
  asiantuntija: 5,
  manager: 6,
  manageri: 6,
  chef: 6,
  director: 7,
  johtaja: 7,
  direktör: 7,
  executive: 8,
  johto: 8,
  företagsledare: 8,
};

function scoreTitle(jobTitle: string | undefined, jobNormalisedTitle: string | undefined, preferredTitles: string[]): number | undefined {
  const candidates = preferredTitles.map((t) => t.toLowerCase().trim()).filter(Boolean);
  if (candidates.length === 0) return undefined;
  const jTitleNorm = (jobNormalisedTitle || '').toLowerCase().trim();
  const jTitle = (jobTitle || '').toLowerCase().trim();
  if (!jTitleNorm && !jTitle) return undefined;

  let best = 0;
  for (const userTitle of candidates) {
    if (jTitleNorm && userTitle === jTitleNorm) {
      best = Math.max(best, 100);
      continue;
    }
    if (jTitleNorm && (jTitleNorm.includes(userTitle) || userTitle.includes(jTitleNorm))) {
      best = Math.max(best, 70);
      continue;
    }
    if (jTitle && userTitle === jTitle) {
      best = Math.max(best, 100);
      continue;
    }
    if (jTitle && (jTitle.includes(userTitle) || userTitle.includes(jTitle))) {
      best = Math.max(best, 60);
      continue;
    }
    const userWords = userTitle.split(/\s+/);
    const jobWords = (jTitleNorm || jTitle).split(/\s+/);
    const commonWords = userWords.filter((w) => jobWords.includes(w));
    if (commonWords.length > 0) {
      best = Math.max(best, 40);
    }
  }
  return best;
}

function scoreRoleLevel(jobRoleLevel: string | undefined, preferredLevels: string[]): number | undefined {
  const candidates = preferredLevels.map((l) => l.toLowerCase().trim()).filter(Boolean);
  const jobLevel = (jobRoleLevel || '').toLowerCase().trim();
  if (candidates.length === 0 || !jobLevel) return undefined;

  let best = 0;
  for (const userLevel of candidates) {
    if (userLevel === jobLevel) {
      best = Math.max(best, 100);
      continue;
    }
    const userLevelNum = ROLE_LEVEL_HIERARCHY[userLevel] || 0;
    const jobLevelNum = ROLE_LEVEL_HIERARCHY[jobLevel] || 0;
    if (userLevelNum > 0 && jobLevelNum > 0) {
      const diff = Math.abs(userLevelNum - jobLevelNum);
      if (diff === 0) best = Math.max(best, 100);
      else if (diff === 1) best = Math.max(best, 70);
      else if (diff === 2) best = Math.max(best, 40);
      else best = Math.max(best, 10);
    }
  }
  return best;
}

function scoreLocation(jobLocations: string[] | undefined, preferredLocations: string[]): number | undefined {
  const userLocations = preferredLocations.map((l) => l.toLowerCase().trim()).filter(Boolean);
  const jobLocs = (jobLocations ?? []).map((l) => l.toLowerCase().trim()).filter(Boolean);
  if (userLocations.length === 0 || jobLocs.length === 0) return undefined;

  const matches = jobLocs.filter((jl) => userLocations.some((ul) => jl.includes(ul) || ul.includes(jl)));
  if (matches.length === 0) return 0;
  return Math.min(100, Math.round((matches.length / jobLocs.length) * 100));
}

function scoreJobType(jobJobType: string | undefined, preferredJobTypes: string[]): number | undefined {
  const candidates = preferredJobTypes.map((t) => t.toLowerCase().trim()).filter(Boolean);
  const jobType = (jobJobType || '').toLowerCase().trim();
  if (candidates.length === 0 || !jobType) return undefined;
  return candidates.some((t) => t === jobType) ? 100 : 0;
}

/**
 * Job pay normalised to a monthly figure so it can be compared with the profile, whose salary
 * fields are monthly (currentJob.salary and desiredJob.salaryMin are both labelled "monthly" in
 * the profile form). A missing salaryPeriod means monthly, matching convertSalaryToMonthlyEUR and
 * the processor's own sanitizer default. Before this normalisation the user's figure was turned
 * into a *yearly* FTE and compared against the listing's raw (usually monthly) range, which scored
 * nearly every monthly listing as far below the user's pay.
 */
export function normalizeJobSalaryToMonthly(value: number, period: DisplayJob['salaryPeriod']): number {
  switch (period) {
    case 'annual':
      return value / 12;
    case 'daily':
      return value * 22;
    case 'hourly':
      return value * 160;
    default:
      return value;
  }
}

/** Threshold-based salary scoring against a minimum acceptable salary (desired job salaryMin). */
function scoreSalaryAgainstMinimum(jobMin: number, jobMax: number, userMin: number): number {
  if (userMin <= 0) return 100;
  if (jobMin >= userMin) return 100;
  if (jobMax >= userMin) return 90;
  const diff = userMin - jobMax;
  const percentDiff = (diff / userMin) * 100;
  if (percentDiff <= 20) return 60;
  if (percentDiff <= 50) return 30;
  return 0;
}

/**
 * Threshold-based scoring of a job value against a baseline (the user's current job).
 * Meeting or exceeding the baseline scores 100; falling short decays by percentage shortfall.
 * Unlike min-max range scoring, this stays meaningful when only two points (user vs. job) exist.
 */
function scoreAgainstBaseline(jobValue: number, baselineValue: number): number {
  if (baselineValue <= 0) return 100;
  if (jobValue >= baselineValue) return 100;
  const diff = baselineValue - jobValue;
  const percentDiff = (diff / baselineValue) * 100;
  if (percentDiff <= 20) return 60;
  if (percentDiff <= 50) return 30;
  return 0;
}

/**
 * Calculate a match score (0-100) for a single job based on user profile and comparison weights.
 * This uses the same logic as jobComparison but adapted for single job scoring.
 * Returns both the overall score and the breakdown.
 */
export function calculateJobMatchScore(
  job: DisplayJob,
  userProfile: JobFormState,
  weights: ComparisonWeights
): MatchScoreBreakdown | null {
  // If user hasn't filled out their profile, return null (not zero)
  // This helps distinguish between "no profile" and "score is 0"
  if (!userProfile.currentJob || (!userProfile.currentJob.salary && !userProfile.currentJob.title && !userProfile.currentJob.normalisedTitle)) {
    return null;
  }

  const currentJob = userProfile.currentJob;
  const desiredJob = userProfile.desiredJob;
  const w: ComparisonWeights = {
    salaryWeight: weights.salaryWeight ?? 0.2,
    vacationWeight: weights.vacationWeight ?? 0.2,
    benefitsWeight: weights.benefitsWeight ?? 0.2,
    remotenessWeight: weights.remotenessWeight ?? 0.2,
    languagesWeight: (weights as ComparisonWeights & { languagesWeight?: number }).languagesWeight ?? 0.2,
    titleWeight: weights.titleWeight ?? 0.15,
    roleLevelWeight: weights.roleLevelWeight ?? 0.1,
    locationWeight: weights.locationWeight ?? 0.1,
    jobTypeWeight: weights.jobTypeWeight ?? 0.05,
  };

  // Convert user profile to DisplayJob format for comparison
  const userJob: DisplayJob = {
    objectID: 'user-current',
    title: currentJob.title || '',
    normalisedTitle: currentJob.normalisedTitle || '',
    normalisedRoleLevel: currentJob.normalisedRoleLevel || '',
    description: currentJob.description || '',
    requirements: [],
    responsibilities: [],
    skillsAndTechnologies: [],
    location: Array.isArray(currentJob.locations) ? currentJob.locations : [],
    remoteness: currentJob.remoteness || '',
    jobType: currentJob.jobType || '',
    // Profile: monthly salary + work %. Scale to a 100% FTE monthly figure; listings are
    // normalised to monthly too (see normalizeJobSalaryToMonthly) so both sides share a unit.
    ...(function () {
      const monthly = typeof currentJob.salary === 'number' ? currentJob.salary : 0;
      const pct = typeof (currentJob as { workPercentage?: number }).workPercentage === 'number'
        ? Math.max(1, Math.min(100, (currentJob as { workPercentage: number }).workPercentage))
        : 100;
      const monthlyFte = monthly * (100 / pct);
      return { salaryRangeMin: monthlyFte, salaryRangeMax: monthlyFte };
    })(),
    salaryDetails: '',
    specialisations: [],
    requiredLanguages: [],
    optionalLanguages: [],
    postedDate: '',
    applicationDeadline: '',
    contactEmail: '',
    companyId: '',
    logoUrl: undefined,
    companyIndustry: undefined,
    employerOverview: undefined,
    companyContactDetails: undefined,
    benefits: Array.isArray(currentJob.benefits) ? currentJob.benefits : [],
    vacationWeeks: typeof currentJob.vacationWeeks === 'number' ? currentJob.vacationWeeks : 0,
    recruitmentProcess: [],
    educationLevel: '',
    minimumYearsOfExperience: 0,
    perks: [],
    url: '',
    processedAt: '',
    firstSeen: '',
    lastSeen: '',
    isActive: true,
    field: '',
    currency: 'EUR',
    score: 0,
  };

  // Salary: threshold check against the desired minimum salary when set, else against the user's current salary
  const desiredSalaryMin = typeof desiredJob?.salaryMin === 'number' ? desiredJob.salaryMin : 0;
  const jobSalaryMin = typeof job.salaryRangeMin === 'number' ? normalizeJobSalaryToMonthly(job.salaryRangeMin, job.salaryPeriod) : 0;
  const jobSalaryMax = typeof job.salaryRangeMax === 'number' ? normalizeJobSalaryToMonthly(job.salaryRangeMax, job.salaryPeriod) : jobSalaryMin;
  const baselineSalary = desiredSalaryMin > 0 ? desiredSalaryMin : (typeof userJob.salaryRangeMin === 'number' ? userJob.salaryRangeMin : 0);
  const salaryScore = scoreSalaryAgainstMinimum(jobSalaryMin, jobSalaryMax, baselineSalary);

  // Vacation: threshold check against the user's current vacation weeks
  const jobVacation = typeof job.vacationWeeks === 'number' ? job.vacationWeeks : 0;
  const baselineVacation = typeof userJob.vacationWeeks === 'number' ? userJob.vacationWeeks : 0;
  const vacationScore = scoreAgainstBaseline(jobVacation, baselineVacation);

  // Remoteness: prefer desired remoteness options when set, else current job's remoteness
  const desiredRemoteness = (desiredJob?.remoteness ?? []).map((r) => r.toLowerCase().trim()).filter(Boolean);
  const jobRemoteness = String(job.remoteness ?? '').toLowerCase().trim();
  let remotenessScore = 0;
  if (desiredRemoteness.length > 0) {
    remotenessScore = Math.max(
      0,
      ...desiredRemoteness.map((r) =>
        r === jobRemoteness ? 100 : r === 'hybrid' || jobRemoteness === 'hybrid' ? 60 : 0
      )
    );
  } else if (jobRemoteness && typeof userJob.remoteness === 'string' && userJob.remoteness) {
    const userRemoteness = userJob.remoteness.toLowerCase();
    if (jobRemoteness === userRemoteness) {
      remotenessScore = 100;
    } else if (userRemoteness === 'hybrid' || jobRemoteness === 'hybrid') {
      remotenessScore = 60;
    } else if (userRemoteness === 'onsite' && jobRemoteness === 'remote') {
      remotenessScore = 80; // Remote is generally preferred
    } else {
      remotenessScore = 0;
    }
  }
  if (!isFinite(remotenessScore) || isNaN(remotenessScore)) remotenessScore = 0;

  // Benefits: threshold check against the number of benefits on the user's current job
  const jobBenefits = Array.isArray(job.benefits) ? job.benefits.length : 0;
  const baselineBenefits = Array.isArray(userJob.benefits) ? userJob.benefits.length : 0;
  const benefitsScore = scoreAgainstBaseline(jobBenefits, baselineBenefits);

  // Languages: compare user's professional languages with job required/optional
  const userLangs = (currentJob.languages ?? []).map((l) => String(l).toLowerCase().trim()).filter(Boolean);
  const required = Array.isArray(job.requiredLanguages) ? job.requiredLanguages : [];
  const optional = Array.isArray(job.optionalLanguages) ? job.optionalLanguages : [];
  let languagesScore = 100;
  if (required.length > 0) {
    const requiredMet = required.filter((r) => userLangs.some((u) => u === String(r).toLowerCase().trim())).length;
    languagesScore = (requiredMet / required.length) * 100;
  } else if (optional.length > 0) {
    const optionalMet = optional.filter((o) => userLangs.some((u) => u === String(o).toLowerCase().trim())).length;
    languagesScore = optionalMet / optional.length * 100;
  }
  if (!isFinite(languagesScore) || isNaN(languagesScore)) languagesScore = 100;

  // Title / role level / location / job type: prefer desired-job preferences, fall back to current job
  const preferredTitles = desiredJob?.titles?.length
    ? desiredJob.titles
    : [currentJob.title, currentJob.normalisedTitle].filter((t): t is string => Boolean(t));
  const titleScore = scoreTitle(job.title, job.normalisedTitle, preferredTitles);

  const preferredRoleLevels = desiredJob?.normalisedRoleLevels?.length
    ? desiredJob.normalisedRoleLevels
    : currentJob.normalisedRoleLevel
      ? [currentJob.normalisedRoleLevel]
      : [];
  const roleLevelScore = scoreRoleLevel(job.normalisedRoleLevel, preferredRoleLevels);

  // TEC-482: desired locations are regionTags placeIds. A job that carries regionTags matches when
  // any of its tags (every ancestor is materialised) is one the user wants. Jobs enriched before
  // the backfill have no regionTags, so they fall back to the label-vs-location-string comparison.
  const desiredRegionTags = desiredJob?.regionTags ?? [];
  const jobRegionTags = Array.isArray(job.regionTags) ? job.regionTags : [];
  let locationScore: number | undefined;
  if (desiredRegionTags.length > 0 && jobRegionTags.length > 0) {
    locationScore = jobRegionTags.some((t) => desiredRegionTags.includes(t)) ? 100 : 0;
  } else {
    const tagLabels = desiredRegionTags.flatMap((t) => [getRegionTagLabel(t, 'en'), getRegionTagLabel(t, 'fi'), getRegionTagLabel(t, 'sv')]);
    const preferredLocations = desiredJob?.locations?.length || tagLabels.length
      ? [...(desiredJob?.locations ?? []), ...tagLabels]
      : currentJob.locations ?? [];
    locationScore = scoreLocation(job.location, preferredLocations);
  }

  const preferredJobTypes = desiredJob?.jobTypes?.length ? desiredJob.jobTypes : currentJob.jobType ? [currentJob.jobType] : [];
  const jobTypeScore = scoreJobType(job.jobType, preferredJobTypes);

  // Calculate weighted overall score. Optional dimensions only contribute when they have a value,
  // and their weight is redistributed proportionally across the dimensions that do.
  const dims: Array<{ score: number | undefined; weight: number }> = [
    { score: salaryScore, weight: w.salaryWeight },
    { score: vacationScore, weight: w.vacationWeight },
    { score: remotenessScore, weight: w.remotenessWeight },
    { score: benefitsScore, weight: w.benefitsWeight },
    { score: languagesScore, weight: w.languagesWeight },
    { score: titleScore, weight: w.titleWeight ?? 0 },
    { score: roleLevelScore, weight: w.roleLevelWeight ?? 0 },
    { score: locationScore, weight: w.locationWeight ?? 0 },
    { score: jobTypeScore, weight: w.jobTypeWeight ?? 0 },
  ];
  const activeDims = dims.filter((d) => typeof d.score === 'number');
  const totalActiveWeight = activeDims.reduce((sum, d) => sum + d.weight, 0);
  let overallScore = 0;
  if (totalActiveWeight > 0) {
    overallScore = activeDims.reduce((sum, d) => sum + (d.score as number) * d.weight, 0) / totalActiveWeight;
  }
  if (!isFinite(overallScore) || isNaN(overallScore)) overallScore = 0;

  return {
    overall: Math.max(0, Math.min(100, Math.round(overallScore))),
    salary: Math.round(salaryScore),
    vacation: Math.round(vacationScore),
    remoteness: Math.round(remotenessScore),
    benefits: Math.round(benefitsScore),
    languages: Math.round(languagesScore),
    weights: w,
    ...(typeof titleScore === 'number' ? { title: Math.round(titleScore) } : {}),
    ...(typeof roleLevelScore === 'number' ? { roleLevel: Math.round(roleLevelScore) } : {}),
    ...(typeof locationScore === 'number' ? { location: Math.round(locationScore) } : {}),
    ...(typeof jobTypeScore === 'number' ? { jobType: Math.round(jobTypeScore) } : {}),
  };
}
